import { MarketPlaceTradeAdvise } from "@lib/types/MarketPlace";
import { Drawer, Descriptions, Skeleton, Typography, Divider } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import { shortenString } from "@lib/helpers/StringHelper";
import styled from "styled-components";
import dayjs from "dayjs";
import { LanguageLabel } from "@lib/components/MiniComponents/Others";
import { AppJson, AppTag } from "@lib/components/AppComponents";
import {
  tradeAdviseActionColor,
  tradeAdviseMarketBiasColor,
  tradeAdviseRiskLevelColor,
  tradeAdviseTypeColor,
} from "@lib/helpers/ColorHelper";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  item: MarketPlaceTradeAdvise | null;
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 10 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

export const MarketPlaceTradeAdviseDrawer = (props: Props) => {
  const { open, onClose, item } = props;
  const { messages } = useIntl();

  return (
    <StyledDrawer
      title={
        <FormattedMessage
          id={"common.previewTitle"}
          values={{
            title: messages["sidebar.marketPlace.tradeAdvises"] as string,
          }}
        />
      }
      onClose={onClose}
      open={open}
      width={700}
      id="market-place-trade-advise-drawer"
    >
      {!item ? (
        <SkeletonContent />
      ) : (
        <>
          <StyledDescriptions bordered column={1}>
            <Descriptions.Item label="ID">
              <Typography.Text copyable={{ text: item?.id }}>
                {shortenString(item?.id, 40)}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.symbol" />}>
              <AppTag color="blue">{item?.symbol}</AppTag>
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.action" />}>
              <AppTag color={tradeAdviseActionColor(item?.action)}>
                {item?.action}
              </AppTag>
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.type" />}>
              <AppTag color={tradeAdviseTypeColor(item?.type)}>
                {item?.type}
              </AppTag>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.marketBias" />}
            >
              <AppTag color={tradeAdviseMarketBiasColor(item?.marketBias)}>
                {item?.marketBias}
              </AppTag>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.riskLevel" />}
            >
              <AppTag color={tradeAdviseRiskLevelColor(item?.riskLevel)}>
                {item?.riskLevel}
              </AppTag>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.holdingPeriod" />}
            >
              {item?.holdingPeriod || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.confidence" />}
            >
              {item?.confidence ? `${item.confidence}%` : "-"}
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.balance" />}>
              {item?.balance || "-"}
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.equity" />}>
              {item?.equity || "-"}
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.volume" />}>
              {item?.volume || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.takeProfit" />}
            >
              {item?.takeProfit || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.stopLoss" />}
            >
              {item?.stopLoss || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.priceOrder" />}
            >
              {item?.priceOrder || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.language" />}
            >
              {item?.language ? (
                <LanguageLabel languageCode={item.language} />
              ) : (
                "-"
              )}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.createdAt" />}
            >
              {item?.createdAt
                ? dayjs(item.createdAt).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.createdBy" />}
            >
              {item?.createdBy || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.updatedAt" />}
            >
              {item?.updatedAt
                ? dayjs(item.updatedAt).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.updatedBy" />}
            >
              {item?.updatedBy || "-"}
            </Descriptions.Item>
          </StyledDescriptions>

          {item?.summary && (
            <>
              <Divider />
              <div style={{ marginTop: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 12 }}>
                  <FormattedMessage id="common.summary" />
                </Typography.Title>
                <Typography.Paragraph>{item.summary}</Typography.Paragraph>
              </div>
            </>
          )}

          {item?.riskNote && (
            <>
              <Divider />
              <div style={{ marginTop: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 12 }}>
                  <FormattedMessage id="common.riskNote" />
                </Typography.Title>
                <Typography.Paragraph>{item.riskNote}</Typography.Paragraph>
              </div>
            </>
          )}

          {item?.extraData && Object.keys(item.extraData).length > 0 && (
            <>
              <Divider />
              <div style={{ marginTop: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 12 }}>
                  <FormattedMessage id="common.extraData" />
                </Typography.Title>
                <AppJson src={item.extraData} />
              </div>
            </>
          )}
        </>
      )}
    </StyledDrawer>
  );
};
